import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addYears,
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  differenceInYears,
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  getDay,
  getDaysInMonth,
  getWeek,
  isAfter,
  isBefore,
  isLeapYear,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfYear,
  subSeconds,
  subYears
} from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Tools } from 'src/app/classes/shared/tools';
import { MetricAddComponent } from 'src/app/components/shared/metric-add/metric-add.component';
import { Metric } from 'src/app/interfaces/api/metric';
import { Project, Schedule } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Extended metric interface with additional display properties for this component
interface SequenceMetric extends Metric {
  left?: number;
  originalLeft?: number;
  width?: number;
  top?: number;
  height?: number;
  color?: string;
  label?: string;
  startDate?: string;
  isDivided?: boolean;
}

interface MetricPosition {
  left: number;
  originalLeft: number;
  width: number;
  top: number;
  height: number;
}

@Component({
  selector: 'app-sequence-calendar',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    DragDropModule,
    TranslateModule
  ],
  templateUrl: './sequence-calendar.component.html',
  styleUrls: ['./sequence-calendar.component.scss']
})
export class SequenceCalendarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild("content", { static: false }) contentContainer!: ElementRef;
  @ViewChild('graph') graph!: ElementRef;
  @ViewChild('board') board!: ElementRef;
  @Input() startDate = subYears(new Date(), 1).toISOString();
  @Input() endDate = new Date().toISOString();
  user!: User;
  project!: Project;
  destroy$ = new Subject<void>();
  metrics: SequenceMetric[] = [];
  years: any[] = [];
  months: any[] = [];
  days: any[] = [];
  weeks: any[] = [];

  hourSize = 0.2;//px
  hourSizes: Record<string, number> = {};
  hourHeight = 200;//px
  totalHoursHeight = 24 * this.hourHeight;
  daySize = this.hourSize * 24;
  dayCount = 0;
  graphWidth: number = 100;
  hoverInfo: boolean = false;
  initialScales: string[] = ["threeYears", "year", 'semester', 'quarter', 'month', 'halfMonth', 'week', 'halfWeek', 'day']
  scales: string[] = [...this.initialScales];
  dayCounts: Record<string, number> = { threeYears: 365 * 3, year: 365, semester: 30 * 3 + 3 * 31, quarter: 30 * 3, month: 31, halfMonth: 15, week: 7, halfWeek: 3, day: 1 }

  // Constantes de rendu partagées entre le TS et le template
  readonly hoursPerDay = 24;
  readonly metricBarWidth = 22;
  readonly segmentMinutes = 15;
  private readonly timeBarRefreshMs = 60000;
  private readonly zoomTargetScale: Record<string, string> = { year: 'semester', month: 'month', week: 'week', day: 'day' };

  calendarWidth: number = 500;
  contentWidth: number = 100;
  currentScale: string = "month";
  scrollLeft: number = 0;
  scrollTop: number = 0;
  hourWidth: number = 50;
  hoursOfDay: { label: string; isHalfHour: boolean, value: string, hidden?: boolean }[] = [];
  startHour: string = startOfDay(new Date()).toISOString();

  mouse: any = { x: 0, y: 0, isDown: false, dX: 0, dY: 0 };

  currentDate: BehaviorSubject<string>;

  scaleIndex: number = 0;
  metric!: SequenceMetric;
  isSelecting: boolean = false;
  mouseX: number = 0;
  mouseY: number = 0;
  selectionHeight: number = 0;
  selectedCells: any[] = [];
  selectedIndex: { hourIndex: string; dayIndex: number; } | null = null;
  currentBarTop!: number;
  dottedBarWidth: any;
  solidBarWidth: any;
  showRawData: boolean = false;
  currentLocale = enUS;

  private currentTimeIntervalId?: ReturnType<typeof setInterval>;
  startDateQuery!: Date;

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent, index?: number) {
    event.stopPropagation();
    if (event.button === 2) {
      const containerBounds = this.graph.nativeElement.getBoundingClientRect();
      this.mouseY = event.clientY - containerBounds.top;
      this.mouseX = event.clientX - containerBounds.left;
      this.mouse.isDown = true;

      if (this.mouseY < 0) {
        this.isSelecting = false;
        return;
      }

      this.isSelecting = true;
      this.selectionHeight = 0;
      event.preventDefault();

      if (index === undefined) return;

      const clickedHour = this.hoursOfDay[index];
      if (!clickedHour) return;

      const hourIndex: string = clickedHour.value;
      const dayIndex = this.getDayIndex(this.mouseX);

      const selectedCell = {
        left: this.getLeftPosition(dayIndex),
        top: this.getTopPosition(hourIndex),
        width: 24,
        height: 50
      };

      this.selectedIndex = {
        hourIndex: hourIndex,
        dayIndex: dayIndex
      };

      this.selectedCells.push(selectedCell);
      return;
    }

    else if (event.button === 0) {
      this.mouse.isDown = true;
      this.mouse.dX = event.x;
      this.mouse.dY = event.y;
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent, index?: number) {
    window.getSelection()?.removeAllRanges();

    if (this.mouse.isDown && event.buttons === 1) {
      const moveFactor = 1;
      this.scrollLeft = Math.max(Math.floor(this.scrollLeft - event.movementX * moveFactor), 0);
      this.graph.nativeElement.scrollTop -= event.movementY * moveFactor;
      this.scrollTop = this.graph.nativeElement.scrollTop;
    }

    if (this.mouse.isDown && event.buttons === 2 && index) {
      if (!this.selectedIndex) {
        const clickedHour = this.hoursOfDay[index];
        if (!clickedHour) return;
        const hourIndex: string = clickedHour.value;
        const dayIndex = this.getDayIndex(this.mouseX);

        this.selectedIndex = {
          hourIndex: hourIndex,
          dayIndex: dayIndex
        };
      }


      const containerBounds = this.graph.nativeElement.getBoundingClientRect();
      const currentY = event.clientY - containerBounds.top;
      this.selectionHeight = Math.abs(currentY - this.mouseY);

      const dayIndex = this.getDayIndex(this.mouseX);
      const clickedHour = this.hoursOfDay[index];
      if (!clickedHour) return;
      const hourIndex: string = clickedHour.value;

      const selectedCell = {
        left: this.getLeftPosition(dayIndex),
        top: this.getTopPosition(hourIndex.toString()),
        width: 24,
        height: 50,
      };

      if (!this.selectedCells.some(cell => cell.left === selectedCell.left && cell.top === selectedCell.top)) {
        this.selectedCells.push(selectedCell);
      }
    }
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    if (this.isSelecting && event.button === 2) {
      this.isSelecting = false;

      if (this.selectedCells.length > 0) {
        event.preventDefault();

        if (!this.selectedIndex) {
          this.selectedCells = [];
          return;
        }


        const startHour = new Date(`2000-01-01T${this.selectedIndex.hourIndex}:00`);
        const endHour = addMinutes(startHour, this.selectedCells.length * this.segmentMinutes);
        const day = format(addDays(this.startDateQuery, this.selectedIndex.dayIndex), 'yyyy-MM-dd');

        const addQuery = { startHour, endHour, day };

        setTimeout(() => {
          this.dialog.open(MetricAddComponent, {
            width: '650px',
            data: {
              metricModels: this.project.metricModels,
              add: addQuery
            }
          }).afterClosed().subscribe(result => {
            if (result != "false") {
              Object.assign(result, this.computeMetricPosition(result.payload.startDate, result.payload.endDate));
              result.color = "#3c8dbc";
              result.label = result.payload.title;

              this.metrics.push(result);
              this.handleOverlappingMetrics(this.metrics);
            }
          })
        }, 0);
      }

      this.selectedIndex = null;
      this.selectedCells = [];
      return;
    }

    this.mouse.isDown = false;
  }

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  @HostListener('mousewheel', ['$event'])
  onMouseWheel(event: any) {
    let className = event.target.className;
    if (className.includes('line') || className.includes('graph-bar')) {
      if (event.wheelDelta > 0) {
        this.setZoom(1, event);
      } else if (event.wheelDelta < 0) {
        this.setZoom(-1, event);
      }
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    let prevHourSize = this.hourSize;
    this.updateHourSizes();
    this.setZoom(0, null, prevHourSize);
  }

  constructor(
    private metricService: MetricService,
    private projectService: ProjectService,
    private auth: AuthService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private tools: Tools) {

    this.user = this.auth.currentUser();
    // Set date-fns locale based on user language
    this.currentLocale = this.user.language === 'fr' ? fr : enUS;
    this.currentDate = new BehaviorSubject(new Date().toISOString());
    this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(project => {
      this.project = project
    })
  }

  // startDate/endDate sont des @Input() : ils ne sont affectés par Angular qu'après le constructeur,
  // donc le chargement des metrics doit être déclenché ici plutôt que dans le constructeur, et refait
  // à chaque changement de période choisie dans le report (date-picker/range-picker du parent).
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['startDate'] && !changes['endDate']) return;

    // La grille affiche toujours le mois complet de la date reçue : les metrics doivent
    // être chargées sur ce même mois complet, pas seulement jusqu'à l'endDate brute reçue.
    this.startDate = startOfMonth(new Date(this.startDate)).toISOString();
    this.endDate = endOfMonth(new Date(this.startDate)).toISOString();
    this.startDateQuery = new Date(this.startDate);
    this.loadMetrics(this.startDate, this.endDate);
  }

  ngAfterViewInit(): void {
    this.currentScale = "month";
    this.updateHourSizes();

    this.currentTimeIntervalId = setInterval(() => {
      this.updateCurrentTimeBar();
    }, this.timeBarRefreshMs);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.currentTimeIntervalId) clearInterval(this.currentTimeIntervalId);
  }

  updateHourSizes(): void {
    this.contentWidth = this.contentContainer.nativeElement.clientWidth;
    this.calendarWidth = this.contentWidth;
    this.hourSizes = {};
    let startMoment = startOfMonth(new Date(this.startDate));
    let endMoment = endOfMonth(new Date(this.endDate));
    let numberOfDays = differenceInDays(endMoment, startMoment) + 4;
    this.scales = [...this.initialScales];
    Object.keys(this.dayCounts).forEach(key => {
      const dayCountForKey = this.dayCounts[key];
      if (dayCountForKey && dayCountForKey > numberOfDays && numberOfDays < 365) {
        let scaleIndex = this.scales.indexOf(key);
        if (scaleIndex >= 0)
          this.scales.splice(scaleIndex, 1);
        return;
      }
      let dayCounts = dayCountForKey && numberOfDays < dayCountForKey ? numberOfDays : dayCountForKey;
      if (dayCounts) {
        this.hourSizes[key] = (this.calendarWidth - this.hourWidth) / (dayCounts * this.hoursPerDay);
      }
    });
    this.hourSize = this.hourSizes[this.currentScale] ?? 0;
  }

  loadMetrics(startDate: string, endDate: string) {
    this.generateHoursOfDay();

    forkJoin({
      // La base contient à la fois "Séquence" (singulier, historique) et "Séquences" (pluriel, cf craneCycle.ts) : on interroge les deux.
      sequences: this.metricService.getMetrics({ names: "Séquence,Séquences", startDate, endDate, limit: 10000 }),
      customMetrics: this.metricService.getMetrics({ custom: true, startDate, endDate, limit: 10000 }),
    }).pipe(takeUntil(this.destroy$)).subscribe(({ sequences, customMetrics }) => {
      const sortedMetrics = sequences.concat(customMetrics).sort((a, b) => {
        const dateA = a.payload.startDate ? new Date(a.payload.startDate).getTime() : 0;
        const dateB = b.payload.startDate ? new Date(b.payload.startDate).getTime() : 0;
        return dateA - dateB;
      });

      // this.metrics doit être renseigné avant setTreeGraph() car celui-ci élargit
      // this.startDate/this.endDate en fonction des dates réelles des metrics
      this.metrics = sortedMetrics;
      this.setTreeGraph();
      this.currentScale = "week";
      this.updateHourSizes();

      this.metrics = this.metrics.map(m => {
        if (!m.payload.startDate || !m.payload.endDate) return m;
        return {
          ...m,
          color: m.payload.color || Config.colors.primary,
          label: m.payload.title,
          startDate: m.payload.startDate,
          ...this.computeMetricPosition(m.payload.startDate, m.payload.endDate),
          isDivided: false,
        };
      });

      this.metrics = this.handleOverlappingMetrics(this.metrics);
      this.setZoom(0);
    });
  }

  // Position/taille d'une barre de metric sur le calendrier, à partir de ses dates
  private computeMetricPosition(startDate: string, endDate: string): MetricPosition {
    const left = this.getLeftFromStartDate(startDate);
    return {
      left: left + 1,
      originalLeft: left,
      width: this.metricBarWidth,
      top: this.getTopFromHour(startDate),
      height: this.getHeightFromDuration(startDate, endDate),
    };
  }

  handleOverlappingMetrics(metrics: SequenceMetric[]): SequenceMetric[] {
    let overlappingMetrics = [];

    metrics.forEach((currentMetric, index) => {
      overlappingMetrics = [currentMetric];

      for (let j = index + 1; j < metrics.length; j++) {
        const nextMetric = metrics[j];
        if (!nextMetric) continue;
        const nextStart = nextMetric.payload.startDate ? new Date(nextMetric.payload.startDate) : new Date();

        if (currentMetric.payload.startDate && currentMetric.payload.endDate &&
          isWithinInterval(nextStart, {
            start: new Date(currentMetric.payload.startDate),
            end: subSeconds(new Date(currentMetric.payload.endDate), 1)
          })) {
          overlappingMetrics.push(nextMetric);
        } else {
          break;
        }
      }

      if (overlappingMetrics.length > 1) {
        const hasCustomMetrics = overlappingMetrics.some(m => m.payload['customMetric'] === true);

        if (hasCustomMetrics) {
          const sharedWidth = (this.metricBarWidth - 1) / 2;

          overlappingMetrics.forEach(m => {
            if (m.payload['customMetric'] && !m.isDivided) {
              m.width = sharedWidth;
              m.left = (m.originalLeft ?? 0) + sharedWidth + 2;
              m.isDivided = true;
            } else {
              m.width = sharedWidth;
            }
          });
        }
      }
    });

    return metrics;
  }

  setTreeGraph() {
    //loop for graph dimensions
    this.metrics.forEach(t => {
      if (t.payload.startDate && isBefore(new Date(t.payload.startDate), new Date(this.startDate))) this.startDate = t.payload.startDate;
      if (t.payload.endDate && isAfter(new Date(t.payload.endDate), new Date(this.endDate))) this.endDate = t.payload.endDate;
    })
    this.currentDate.next(this.endDate)
    this.startDate = startOfMonth(new Date(this.startDate)).toISOString();
    this.endDate = endOfMonth(new Date(this.endDate)).toISOString();
    this.dayCount = differenceInDays(new Date(this.endDate), new Date(this.startDate)) + 1;
    this.graphWidth = this.dayCount * this.daySize;
    this.setLines();
  }

  generateHoursOfDay() {
    const mondayHours = this.getDaySchedule(this.project.schedule, 'mon');

    if (mondayHours) {
      // Parse HH:mm format and create a Date object for the start hour
      const [hours] = mondayHours.startTime.split(':').map(Number);
      const startHourDate = new Date();
      startHourDate.setHours(hours ?? 0, 0, 0, 0);
      this.startHour = startHourDate.toISOString();
    }

    const endHour = mondayHours
      ? (() => {
        const [hours] = mondayHours.endTime.split(':').map(Number);
        const endHourDate = new Date();
        endHourDate.setHours(hours ?? 0, 0, 0, 0);
        return endHourDate;
      })()
      : endOfDay(new Date());

    const totalSegments = differenceInMinutes(endHour, new Date(this.startHour)) / this.segmentMinutes;
    this.totalHoursHeight = totalSegments * 50 + 50;

    this.hoursOfDay = [];
    for (let i = 0; i <= totalSegments; i++) {
      const currentTime = addMinutes(new Date(this.startHour), i * this.segmentMinutes);
      const minute = currentTime.getMinutes();

      this.hoursOfDay.push({
        label: (minute === 0 || minute === 30) ? format(currentTime, 'HH:mm', { locale: this.currentLocale }) : '',
        isHalfHour: minute === 30,
        value: format(currentTime, 'HH:mm', { locale: this.currentLocale })
      });
    }
  }

  getDaySchedule(schedule: Schedule, day: string) {
    const daySchedule = schedule[day as keyof Schedule];
    if (!daySchedule || !daySchedule.enable) {
      return null;
    }
    const times = daySchedule.times[0];
    if (!times) {
      return null;
    }
    const startTime = times[0];
    const endTime = times[1];

    if (!startTime || !endTime) {
      return null;
    }
    return { startTime, endTime };
  }

  updateDayLine(start: string, end: string) {
    this.days = [];
    let numberOfDays = differenceInDays(new Date(end), new Date(start));
    for (let i = 0; i <= numberOfDays; i++) {
      let label: any;
      if (this.isSmallerThan("halfMonth")) {
        label = format(addDays(new Date(this.startDate), i), 'eee dd/MM', { locale: this.currentLocale });
      } else {
        label = addDays(new Date(this.startDate), i).getDate();
      }

      this.days.push({
        label: label,
        position: this.hoursPerDay * i
      });
    }
  }

  updateWeekLine(start: string, end: string) {
    this.weeks = [];

    let startMoment = new Date(start);
    let endMoment = new Date(end);
    let startWeek = getWeek(startMoment);

    // Calculate total days in the range
    let totalDays = differenceInDays(endMoment, startMoment) + 1;
    let remainingDays = totalDays;
    let position = 0;
    let weekIndex = 0;

    while (remainingDays > 0) {
      let weekSize = 7;

      if (weekIndex === 0) {
        // First week: calculate days from start date to end of that week
        let dayOfWeek = getDay(startMoment);
        // Adjust for Monday = 1, Sunday = 0 (date-fns uses Sunday = 0)
        dayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
        weekSize = Math.min(8 - dayOfWeek, remainingDays); // Days remaining in first week
      } else {
        // Subsequent weeks: full week or remaining days
        weekSize = Math.min(7, remainingDays);
      }

      let size = weekSize * this.hoursPerDay;

      this.weeks.push({
        label: `${this.translate.instant('date.week')} ${startWeek + weekIndex}`,
        shortLabel: `S${startWeek + weekIndex}`,
        size,
        position
      });

      remainingDays -= weekSize;
      position += size;
      weekIndex++;
    }
  }

  updateMonthLine(start: string, end: string) {
    this.months = [];
    let numberOfMonths = differenceInMonths(startOfMonth(new Date(end)), startOfMonth(new Date(start)));
    let position = 0;
    for (let i = 0; i <= numberOfMonths; i++) {
      let monthDate = addMonths(new Date(this.startDate), i);
      let dayCount = getDaysInMonth(monthDate);
      let size = dayCount * this.hoursPerDay;
      let label = format(monthDate, 'MMM yyyy', { locale: this.currentLocale }); // TODO: Remove YYYY if period is over the same year
      label = label.charAt(0).toUpperCase() + label.slice(1);
      this.months.push({
        label: label.replace(".", ""),
        size,
        position
      })
      position += size;
    }
  }

  updateYearLine(start: string, end: string) {
    this.years = [];
    let startMoment = new Date(start);
    let endMoment = new Date(end);
    let numberOfYears = differenceInYears(startOfYear(endMoment), startOfYear(startMoment)) + 1;

    let size = 0, position = 0;

    for (let i = 0; i < numberOfYears; i++) {
      let currentYearStart = startOfYear(addYears(startMoment, i));
      let currentYearEnd = endOfYear(currentYearStart);

      let yearSize; // Number of days in this year's portion

      if (i === 0) {
        // First year: Start from the actual start date
        yearSize = differenceInDays(currentYearEnd, startMoment) + 1;
      } else if (i === numberOfYears - 1) {
        // Last year: End at the actual end date
        yearSize = differenceInDays(endMoment, currentYearStart) + 1;
      } else {
        // Full year
        yearSize = isLeapYear(currentYearStart) ? 366 : 365;
      }

      size = yearSize * this.hoursPerDay;
      this.years.push({
        label: format(currentYearStart, 'yyyy', { locale: this.currentLocale }),
        size,
        position
      });

      position += size;
    }
  }

  setLines() {
    if (this.currentScale != "month") {
      this.metrics.forEach(l => l.payload.color = Config.timelineColors.success);
    }
    this.updateMonthLine(this.startDate, this.endDate);
    this.updateWeekLine(this.startDate, this.endDate);
    if (!this.days.length || this.isSmallerThan('quarter')) {
      this.updateDayLine(this.startDate, this.endDate);
    }
    if (this.isGreaterThan('month')) {
      this.days = [];
    }

    if (this.isGreaterThan("year")) {
      this.updateYearLine(this.startDate, this.endDate);
      this.months = [];
      this.weeks = [];
    } else
      this.years = [];
  }

  getDateFromLeft(left: number) {
    return addHours(new Date(this.startDate), left / this.hourSize).toISOString();
  }

  // Offset vertical (en minutes depuis le début de la journée de travail) pour une heure de référence donnée
  private getScheduleDayStart(reference: Date): Date {
    const dayStart = new Date(reference);
    dayStart.setHours(new Date(this.startHour).getHours(), 0, 0, 0);
    return dayStart;
  }

  getTopFromHour(startDate: string): number {
    const startDateObj = new Date(startDate);
    const minute = differenceInMinutes(startDateObj, this.getScheduleDayStart(startDateObj));
    return minute * this.hourHeight / 60;
  }

  getHeightFromDuration(startDate: string, endDate: string): number {
    const minute = differenceInMinutes(new Date(endDate), new Date(startDate));
    return minute * this.hourHeight / 60;
  }

  getLeftFromStartDate(startDate: string) {
    const startOfProject = startOfDay(new Date(this.startDate));  // Début du projet
    const diff = differenceInDays(new Date(startDate), startOfProject);  // Différence en jours
    return this.hoursPerDay * Math.floor(diff);
  }

  getScaled(value: number) {
    return (value < 1 ? Math.ceil(value * this.hourSize) : value * this.hourSize);
  }

  onClickMetric(metric: SequenceMetric) {
    if (!metric.payload['customMetric']) return;

    this.dialog.open(MetricAddComponent, {
      width: '650px',
      data: {
        metricModels: this.project.metricModels,
        edit: metric
      }
    }).afterClosed().subscribe(result => {
      if (result != "false") {
        Object.assign(result, this.computeMetricPosition(result.payload.startDate, result.payload.endDate));

        this.metrics[this.metrics.findIndex(m => m.id == result.id)] = result;
        this.handleOverlappingMetrics(this.metrics);
      }
    })
  }

  onMouseEnterInfo(event: MouseEvent, metric: SequenceMetric) {
    this.hoverInfo = true;
    this.metric = metric;
  }

  onMouseLeaveInfo() {
    this.hoverInfo = false;
  }

  setZoom(val: number, event: any = null, prevHourSize: number | null = null) {
    if (this.hourSize != prevHourSize) {
      let boardBound = this.board.nativeElement.getBoundingClientRect();
      let boardLeft = boardBound.left;
      let mouseX = event ? (event as MouseEvent).clientX : 0;
      let mouseLeft = mouseX - boardLeft;
      if (mouseLeft < 0) mouseLeft = 0;
      prevHourSize = prevHourSize ?? this.hourSize;

      this.scaleIndex += val;
      if (this.scaleIndex == -1) this.scaleIndex = 0;
      if (this.scaleIndex >= this.scales.length) this.scaleIndex = this.scales.length - 1;

      this.currentScale = this.scales[this.scaleIndex] ?? 'month';
      this.hourSize = this.hourSizes[this.currentScale] ?? 0;
      this.daySize = this.hourSize * this.hoursPerDay;
      let scaleRatio = this.hourSize / prevHourSize;
      let mouseGraphLeft = this.scrollLeft + mouseLeft;
      let nextMouseGraphLeft = mouseGraphLeft * scaleRatio;

      this.scrollLeft = Math.max(Math.floor(nextMouseGraphLeft - mouseLeft), 0);
      this.graphWidth = this.dayCount * this.daySize;
      this.setLines();
      this.updateCurrentTimeBar();
    }
  }

  getDayIndex(mouseX: number): number {
    return Math.floor(mouseX / (this.hourSize * this.hoursPerDay));
  }

  getLeftPosition(dayIndex: number): number {
    return dayIndex * this.hoursPerDay;
  }

  getTopPosition(hourIndex: string): number {
    const minutes = this.getMinutesFromStart(hourIndex);
    return minutes * this.hourHeight / 60;
  }

  getMinutesFromStart(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    const selectedTime = new Date();
    selectedTime.setHours(hours ?? 0, minutes ?? 0, 0, 0);

    return differenceInMinutes(selectedTime, this.getScheduleDayStart(selectedTime));
  }

  // Is currentScale (e.g. month) smaller than scale argument (e.g. quarter). month is smaller than quarter => yields true
  isSmallerThan(scale: string) {
    let scaleIndex = this.scales.findIndex(s => s == scale);
    if (scaleIndex >= 0)
      return scaleIndex < this.scaleIndex;
    return true;
  }

  // Is currentScale (e.g. month) greater than scale argument (e.g. week). month is greater than week => yields true
  isGreaterThan(scale: string) {
    let scaleIndex = this.scales.findIndex(s => s == scale);
    if (scaleIndex >= 0)
      return scaleIndex > this.scaleIndex;
    return false;
  }

  trackByDay(index: number, day: any): any {
    return day.id || index;
  }

  updateCurrentTimeBar(): void {
    const now = new Date();

    if (isWithinInterval(now, { start: startOfDay(this.startDate), end: endOfDay(this.endDate) })) {
      this.dottedBarWidth = this.getLeftFromStartDate(now.toISOString());
      this.solidBarWidth = this.hoursPerDay;
    } else {
      this.dottedBarWidth = 0;
      this.solidBarWidth = 0;
    }
    this.currentBarTop = this.getTopFromHour(now.toISOString());
  }

  onZoom(type: string, period: any): void {
    const targetScale = this.zoomTargetScale[type];
    if (!targetScale) {
      console.warn('Unsupported period type:', type);
      return;
    }

    const zoomSteps = this.getZoomSteps(targetScale);

    if (zoomSteps !== 0) {
      this.setZoom(zoomSteps);
    }

    this.scrollLeft = period.position * this.hourSize;
  }

  getZoomSteps(targetScale: string): number {
    const targetIndex = this.scales.indexOf(targetScale);
    if (targetIndex === -1) {
      console.warn(`Target scale "${targetScale}" not found in scales`, this.scales);
      return 0;
    }
    return targetIndex - this.scaleIndex;
  }

  getBatteryColor(trackerBattery: number): string {
    return this.tools.getBatteryColor(trackerBattery);
  }

  getShackleColor(shackleBattery: number): string {
    return this.tools.getShackleColor(shackleBattery);
  }

  onImgClick(): void {
    // Handle image click
  }

  onRawDataClick(): void {
    this.showRawData = !this.showRawData;
  }
}
